import FJConfig from '../config/FJConfig';
import { FJDecoderManager, FJEncoderManager, FJEncodeMode, FJDecodeMode } from '@fj/codec';
import FJUtil from '../util/FJUtil';
import FJPoolStore from './FJPoolStore';

let _isInit = false;

function init() {
    if (_isInit) return;
    _isInit = true;

    const basePath = `${FJConfig.libPath}codec/${FJConfig.codecVersion}/`;
    const wasmPath = `${FJConfig.libPath}/wasm/${FJConfig.wasmVersion}/`;
    const encoderWorkerPath = `${basePath}/encoder.worker.js`;
    const decoderWorkerPath = `${basePath}/decoder.worker.js`;
    const audioProcessorWorkerPath = `${basePath}/audio-processor.worker.js`;
    const webcodecWorkerPath = `${basePath}/webcodec.worker.js`;

    FJEncoderManager.init(encoderWorkerPath, webcodecWorkerPath, wasmPath, `${wasmPath}codec.js`);
    FJDecoderManager.init(decoderWorkerPath, wasmPath, `${wasmPath}codec.js`, audioProcessorWorkerPath);
}

/**
 * @desc 裁剪视频并导出
 * @param {File} file 视频文件
 * @param {Object} options 裁剪选项
 * @param {Number} options.start 开始时间
 * @param {Number} options.end 结束时间
 * @param {Boolean} options.isCompress 是否压缩
 * @param {Object} options.size 导出尺寸
 * @param {Number} options.size.width 导出宽度
 * @param {Number} options.size.height 导出高度
 * @param {Function} options.onProgress 进度回调，回调参数 {Number} progress - 进度，0 - 1
 * @param {Function} options.onComplete 完成回调，回调参数 {Blob} blob - 导出的视频文件
 * @param {Function} options.onError 错误回调，回调参数 {Error} error - 错误信息
 */
function trimVideoAndExport(file, options) {
    const {
        start: optionsStart,
        end: optionsEnd,
        isCompress = false,
        size = { width: 1080, height: 1080 },
        onProgress = () => {},
        onComplete = () => {},
        onError = () => {},
    } = options || {};

    const start = optionsStart || 0;
    const end = optionsEnd || Infinity;

    (async () => {
        const audioConfig = {
            bitrate: 256 * 1000,
            samplerate: FJUtil.getAudioSampleRate(),
        };

        const {
            width: sourceWidth,
            height: sourceHeight,
            frameRate,
            hasAudio,
            hasVideo,
            rotate,
            duration: decodeDuration,
        } = await getVideoInfo(file);

        if (!hasVideo) {
            onError(new Error('No video found'));
            return;
        }

        const FPS = frameRate;
        const isRotate = rotate === 90 || rotate === 270;
        const exportVideoSize = isCompress
            ? {
                  width: size.width,
                  height: size.height,
              }
            : { width: isRotate ? sourceHeight : sourceWidth, height: isRotate ? sourceWidth : sourceHeight };

        const videoConfig = {
            fps: FPS,
            width: exportVideoSize.width,
            height: exportVideoSize.height,
            presetIdx: 0, // h264编码参数，控制输出速度和质量。0代表最快，0-7依次对应"ultrafast", "superfast", "veryfast", "faster", "fast", "medium", "slow", "veryslow"
            codecIdx: 1, // 编码器索引，0代表vpx，1代表h264, 2代表gif
            formatIdx: 1, // 格式索引，0代表webm，1代表mp4, 2代表gif
            loop: 0, // GIF编码参数，0代表无限循环播放, 1代表播放一次
            mode: FJEncodeMode.WEBCODECS,
            quality: {
                crf: 20,
                bitrate: 10000000,
            },
            bitrateMode: 'variable', // h264编码参数，控制输出速度和质量。constant代表恒定码率，variable代表可变码率
            exportQuality: 1080,
            concurrentSize: 2,
        };

        await new Promise(resolve => {
            const encoderOptions = {
                initCallback: resolve,
                doneCallback: data => {
                    const blob = new Blob([data], { type: 'video/mp4' });
                    onComplete(blob);
                    FJEncoderManager.cancel();
                },
                chunkCallback: () => {},
                errorCallback: onError,
                encodeCallback: () => {},
            };
            FJEncoderManager.initEncoder(videoConfig, audioConfig, encoderOptions);
        });

        if (hasAudio) {
            FJEncoderManager.markAudioEncode(true);
        }

        const config = {
            fileName: file.name,
            width: exportVideoSize.width,
            height: exportVideoSize.height,
            start,
            end,
            speed: 1,
            fps: FPS,
            samplerate: FJUtil.getAudioSampleRate(),
            mode: FJUtil.getBrowser() === 'FF' ? FJDecodeMode.FFMPEG : FJDecodeMode.WEBCODECS,
            frameType: 'VIDEOFRAME',
            ignoreNoValidDuration: true,
        };

        let count = 0;
        let timestampPerFrame = (1000 * 1000) / FPS;
        let maxCount = 0;
        if (end === Infinity) {
            let duration = decodeDuration;
            if (duration <= 0) {
                duration = (await FJDecoderManager.getVideoInfoByElement(file))?.duration || Infinity;
            }
            maxCount = (duration - start) * FPS;
        } else {
            maxCount = (end - start) * FPS;
        }

        const exportCanvas = FJPoolStore.getCanvas();
        const exportCtx = exportCanvas.getContext('2d');

        exportCanvas.width = exportVideoSize.width;
        exportCanvas.height = exportVideoSize.height;

        // 解码视频
        await new Promise((resolve, reject) => {
            FJDecoderManager.decodeVideo(file, config, {
                decodeCallback: frame => {
                    const { data, error, ended } = frame;
                    if (error) {
                        reject(error);
                        return;
                    }

                    if (ended) {
                        FJEncoderManager.markVideoFrameDecodeDone();
                        FJPoolStore.releaseCanvas(exportCanvas);
                        resolve();
                        return;
                    }

                    const { frameWidth, frameHeight, data: imageSourceFromData, type } = data;

                    let imageSource = imageSourceFromData;
                    if (type === 'ARRAYBUFFER' || type === 'TYPEDARRAY') {
                        // eslint-disable-next-line no-undef
                        imageSource = new VideoFrame(imageSourceFromData, {
                            format: 'RGBX',
                            alpha: 'discard',
                            codedWidth: frameWidth,
                            codedHeight: frameHeight,
                            displayWidth: frameWidth,
                            displayHeight: frameHeight,
                            duration: timestampPerFrame,
                            timestamp: timestampPerFrame * ++count,
                        });
                    }

                    let sourceWidth = frameWidth;
                    let sourceHeight = frameHeight;
                    let exportImageSource = imageSource;

                    if (sourceWidth !== exportVideoSize.width || sourceHeight !== exportVideoSize.height) {
                        exportCanvas.width = exportVideoSize.width;
                        exportCanvas.height = exportVideoSize.height;

                        exportCtx.drawImage(imageSource, 0, 0, exportVideoSize.width, exportVideoSize.height);
                        exportImageSource = exportCanvas;
                    }

                    // eslint-disable-next-line no-undef
                    const vf = new VideoFrame(exportImageSource, {
                        // format: 'RGBX',
                        alpha: 'discard',
                        displayWidth: sourceWidth,
                        displayHeight: sourceHeight,
                        duration: timestampPerFrame,
                        timestamp: timestampPerFrame * ++count,
                    });

                    FJEncoderManager.addVideoFrame({ type: 'VIDEOFRAME', data: vf });
                    imageSource.close && imageSource.close();
                    onProgress(count / maxCount);
                },
                getValidFramesSize: () => {
                    return FJEncoderManager.getValidFramesSize();
                },
            });
        });

        if (hasAudio) {
            // 编码音频
            const audioData = await FJDecoderManager.decodeAudioOverall(file, {
                start,
                end,
                samplerate: FJUtil.getAudioSampleRate(),
                mode: FJDecodeMode.FFMPEG,
                speed: 1,
                fileType: 'video',
                ignoreNoValidDuration: true,
            });

            FJEncoderManager.markAudioEncode(false);
            FJEncoderManager.addAudioChunk(audioData.buffer, true);
        }
    })().catch(e => onError(e));
}

/**
 * @desc 获取视频信息
 * @param {File} file 视频文件
 * @param {Number} timeout 超时时间
 * @returns {Promise<Object>} 视频信息 - 返回对象可能包含以下属性：
 * - width: 视频宽度
 * - height: 视频高度
 * - frameRate: 视频帧率
 * - hasAudio: 是否包含音频
 * - hasVideo: 是否包含视频
 * - rotate: 视频旋转角度
 */
async function getVideoInfo(file, timeout = 3000) {
    let data = await Promise.race([
        new Promise(resolve => {
            FJDecoderManager.getVideoInfo(file, {
                ignoreNoValidDuration: true,
            })
                .then(resolve)
                .catch(e => resolve({ error: e }));
        }),
        new Promise(resolve => {
            setTimeout(() => {
                resolve({ error: new Error('Timeout') });
            }, timeout);
        }),
    ]);

    const getDataInfo = data => {
        return {
            width: data.width,
            height: data.height,
            frameRate: data.frameRate,
            hasAudio: data.hasAudio,
            hasVideo: data.hasVideo,
            rotate: data.rotate,
            duration: data.duration,
        };
    };

    if (!data.error) {
        return getDataInfo(data);
    }

    // 如果获取视频信息失败，则使用element获取视频信息
    // 此时 frameRate 固定为30，hasAudio 固定为true，hasVideo 固定为true，rotate 固定为0
    data = await Promise.race([
        new Promise(resolve => {
            FJDecoderManager.getVideoInfoByElement(file)
                .then(data =>
                    resolve({
                        width: data.width,
                        height: data.height,
                        duration: data.duration,
                        frameRate: 30,
                        hasAudio: true,
                        hasVideo: true,
                        rotate: 0,
                    }),
                )
                .catch(e => resolve({ error: e }));
        }),
        new Promise(resolve => {
            setTimeout(() => {
                resolve({ error: new Error('Timeout') });
            }, timeout);
        }),
    ]);

    if (data.error) {
        throw data.error;
    }

    return getDataInfo(data);
}

export default { init, trimVideoAndExport };
